import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface RenovationContent {
  title: string;
  subtitle: string;
  description: string;
  projectsTitle: string;
  project1Name: string;
  project1Description: string;
  project2Name: string;
  project2Description: string;
  project3Name: string;
  project3Description: string;
  impactTitle: string;
  impact1: string;
  impact2: string;
  impact3: string;
  levelsTitle: string;
  level1Name: string;
  level1Amount: string;
  level2Name: string;
  level2Amount: string;
  level3Name: string;
  level3Amount: string;
}

export default function RenovationManager() {
  const [content, setContent] = useState<RenovationContent>({
    title: 'Renovation Fund - Preserve Our Sacred Space',
    subtitle: 'Help maintain and enhance our temple',
    description: 'The temple is a timeless sanctuary that requires continuous care and maintenance. Your support helps us preserve the architectural beauty and spiritual sanctity of this sacred space.',
    projectsTitle: 'Current Renovation Projects',
    project1Name: 'Sanctum Roof Repairs',
    project1Description: 'Essential repairs to the inner sanctum roof to prevent water damage and preserve the sacred chamber.',
    project2Name: 'Tower Restoration',
    project2Description: 'Restoration of temple towers and architectural elements to maintain their structural integrity.',
    project3Name: 'Courtyard Improvements',
    project3Description: 'Paving and lighting improvements to enhance safety and accessibility in temple courtyards.',
    impactTitle: 'How Your Contribution Helps',
    impact1: '₹1000 supports emergency repairs',
    impact2: '₹5000 contributes to major restoration projects',
    impact3: '₹10000 sponsors a complete renovation phase',
    levelsTitle: 'Donation Levels',
    level1Name: 'Supporter',
    level1Amount: '₹1000',
    level2Name: 'Contributor',
    level2Amount: '₹5000',
    level3Name: 'Sponsor',
    level3Amount: '₹10000'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, []);

  const loadContent = () => {
    try {
      console.log('🔄 RenovationManager: Setting up real-time listener...');
      const docRef = doc(db, 'renovation_content', 'main');
      setLoading(true);
      
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data() as RenovationContent;
            setContent(data);
            console.log('✅ Admin: Real-time content loaded from Firebase');
          } else {
            console.warn('⚠️ Admin: Document does not exist, using defaults');
          }
        } catch (error) {
          console.error('Error processing content:', error);
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('❌ Admin Error in onSnapshot:', error);
        setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
        setLoading(false);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
      setLoading(false);
      return () => {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving renovation content to Firebase...');
      const docRef = doc(db, 'renovation_content', 'main');
      await setDoc(docRef, content);
      console.log('✅ Content saved successfully to renovation_content/main');
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('❌ Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof RenovationContent, value: string) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Renovation Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage the renovation fund page content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📄 Main Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Title" value={content.title} onChange={(e) => handleChange('title', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Subtitle" value={content.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={content.description} onChange={(e) => handleChange('description', e.target.value)} multiline rows={3} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🏗️ Current Projects
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.projectsTitle} onChange={(e) => handleChange('projectsTitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={i}>
              <TextField fullWidth label={`Project ${i} Name`} value={content[`project${i}Name` as keyof RenovationContent]} onChange={(e) => handleChange(`project${i}Name` as keyof RenovationContent, e.target.value)} size="small" sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
              <TextField fullWidth label={`Project ${i} Description`} value={content[`project${i}Description` as keyof RenovationContent]} onChange={(e) => handleChange(`project${i}Description` as keyof RenovationContent, e.target.value)} multiline rows={2} sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          💡 Your Impact
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.impactTitle} onChange={(e) => handleChange('impactTitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={i}>
              <TextField fullWidth label={`Impact ${i}`} value={content[`impact${i}` as keyof RenovationContent]} onChange={(e) => handleChange(`impact${i}` as keyof RenovationContent, e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          💰 Donation Levels
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Section Title" value={content.levelsTitle} onChange={(e) => handleChange('levelsTitle', e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
          </Grid>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={i}>
              <TextField fullWidth label={`Level ${i} Name`} value={content[`level${i}Name` as keyof RenovationContent]} onChange={(e) => handleChange(`level${i}Name` as keyof RenovationContent, e.target.value)} size="small" sx={{ mb: 1, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
              <TextField fullWidth label={`Level ${i} Amount`} value={content[`level${i}Amount` as keyof RenovationContent]} onChange={(e) => handleChange(`level${i}Amount` as keyof RenovationContent, e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }} />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity as 'success' | 'error'} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
